// src/store/subscriptionStore.ts

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { subscribeToNotifications } from '../lib/supabase/notifications';
import { useNotificationStore } from './notificationStore';

interface SubscriptionState {
  isSubscribed: boolean;
  unsubscribe: (() => void) | null;
  initializeSubscription: () => Promise<void>;
  cleanupSubscription: () => void;
}

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  isSubscribed: false,
  unsubscribe: null,

  initializeSubscription: async () => {
    const { isSubscribed, unsubscribe } = get();
    if (isSubscribed) {
      console.log('🔔 Notification subscription already active');
      return;
    }

    try {
      // 1. Fetch current notifications so unreadCount is set (fires badge bridge)
      console.log('🔔 Fetching initial notifications for badge update...');
      await useNotificationStore.getState().fetchNotifications();

      // 2. Clean up any existing subscription
      if (unsubscribe) {
        unsubscribe();
      }

      // 3. Get current authenticated user
      const {
        data: { user },
        error: userError
      } = await supabase.auth.getUser();
      if (userError || !user) {
        console.warn('No authenticated user for subscription');
        return;
      }

      // 4. Subscribe to real‑time inserts
      console.log('🔔 Initializing real‑time notification subscription...');
      const newUnsub = await subscribeToNotifications((notification) => {
        // Add each new notification into the notification store
        useNotificationStore.getState().addNotificationToState(notification);
      });

      if (!newUnsub) {
        throw new Error('Failed to initialize notification subscription');
      }

      // 5. Mark as subscribed
      set({
        isSubscribed: true,
        unsubscribe: newUnsub
      });
      console.log('✅ Notification subscription active');
    } catch (error) {
      console.error('❌ Error initializing notification subscription:', error);
      // Reset subscription state on failure
      set({
        isSubscribed: false,
        unsubscribe: null
      });
      throw error;
    }
  },

  cleanupSubscription: () => {
    const { unsubscribe } = get();
    if (unsubscribe) {
      unsubscribe();
      set({
        isSubscribed: false,
        unsubscribe: null
      });
      console.log('🔔 Notification subscription cleaned up');
    }
  }
}));
